/**
 * Stock against size availability, by door — the network-health cockpit's doors page,
 * `/inseason/network-health/doors?runId=` (bata-ui issue 308, owner rulings 2026-10-06 on issue
 * 307; `docs/adr/6015`). Pure, but for the export's one read.
 *
 * The window is the run's: the 13 complete weeks up to its stock as-of week, which the platform
 * derives from the run (bata-platform 570). So the page follows the run the cockpit has open —
 * DEC-99's newest default (ADR 9039), unchanged — and no date is defaulted.
 *
 * A door with no sales in the window has no weeks of cover, so it is not a point: the platform
 * counts those (`doorsWithoutSales`) and the page says how many were left out. A door with sales
 * but nothing offered has no availability and is listed in the table, never drawn at zero.
 *
 * No chart import here: the manifest imports this file for its export, and the manifest is in
 * every page's first bundle. The finding the title states is computed on the page, from the
 * chart's own trend.
 */

import { callOperation } from '@/api';
import {
  DOOR_AVAILABILITY_READ, doorAvailabilityOf, doorAvailabilityParams, type DoorAvailability, type DoorAvailabilityRead,
  type DoorRow, type ObservationBasis,
} from '@/api/pending/service-tradeoff';
import { buildVersion, NO_PRINCIPAL, NOT_PUBLISHED, type ExportCell, type ExportColumn, type ExportRequest, type ProvenanceFilter } from '@/export';
import type { ExportScope } from '@/features/manifest';

import { readNetworkHealthScope, NETWORK_HEALTH_PATH } from './routes';

export const DOORS_PATH = `${NETWORK_HEALTH_PATH}/doors`;
export const DOORS_EXPORT_ID = 'inseason.networkHealth.doors';

/** The doors page of a run. */
export function doorsHref(runId: string | null): string {
  return runId === null ? DOORS_PATH : `${DOORS_PATH}?runId=${encodeURIComponent(runId)}`;
}

/** What the page says about how the window's stock was observed — one catalogue key per basis. */
export const BASIS_NOTE_KEY: Readonly<Record<ObservationBasis | 'unstated', string>> = {
  weekly_snapshot: 'inseason.doors.basis.weekly_snapshot',
  daily: 'inseason.doors.basis.daily',
  mixed: 'inseason.doors.basis.mixed',
  unstated: 'inseason.doors.basis.unstated',
};

/**
 * `?doors=drawn|noSales` — the doors table narrowed to the doors the facts line counts (ADR 9069): «N drawn» are the doors that can be a
 * point (`isDrawable`) and «N with no sales left out» those with no weeks of cover (the platform's `doorsWithoutSales`). The read is the
 * whole window and is not paged, so the page narrows its own rows. No column filter expresses the first (a cover above nothing AND an
 * availability), so the narrowing is a declared parameter.
 */
export type DoorSet = 'drawn' | 'noSales';

/** The set of doors the address names, or `null` for every door. Pure. */
export function readDoorSet(search: string): DoorSet | null {
  const value = new URLSearchParams(search).get('doors');
  return value === 'drawn' || value === 'noSales' ? value : null;
}

/** Whether a door is in the set: drawn is `isDrawable`; no sales is a door with no weeks of cover. Pure. */
export function inDoorSet(door: DoorRow, set: DoorSet): boolean {
  return set === 'drawn' ? isDrawable(door) : door.cover === null;
}

/** The id of the doors table: where the facts line's counts scroll to. */
export const DOORS_TABLE_ID = 'doors-table';

/** A door that can be a point: weeks of cover above zero, an availability. */
export function isDrawable(door: DoorRow): door is DoorRow & { readonly cover: number; readonly availability: number } {
  return door.cover !== null && door.cover > 0 && door.availability !== null;
}

/** Every door's figures pooled: Σ in stock ÷ Σ offered, Σ on hand ÷ Σ sold. Pure. */
export function pooled(doors: readonly DoorRow[]): { readonly availability: number | null; readonly cover: number | null } {
  const offered = doors.reduce((sum, door) => sum + door.sizesOffered, 0);
  const inStock = doors.reduce((sum, door) => sum + door.sizesInStock, 0);
  const sold = doors.reduce((sum, door) => sum + door.sold, 0);
  const onHand = doors.reduce((sum, door) => sum + door.onHandPairWeeks, 0);
  return { availability: offered > 0 ? inStock / offered : null, cover: sold > 0 ? onHand / sold : null };
}

/** The order the table opens in: the least available first, a door with no availability last. Pure. */
export function rankDoors(doors: readonly DoorRow[]): readonly DoorRow[] {
  return [...doors].sort((a, b) => {
    if (a.availability === null || b.availability === null) return a.availability === null ? (b.availability === null ? 0 : 1) : -1;
    return a.availability - b.availability || a.storeKey.localeCompare(b.storeKey);
  });
}

// ── the route's export ───────────────────────────────────────────────────────

export const DOOR_COLUMNS: readonly ExportColumn[] = [
  { key: 'store_key', headerKey: 'inseason.doors.column.storeKey', kind: 'text', descriptionKey: 'inseason.doors.columnGuide.storeKey' },
  { key: 'store_name', headerKey: 'inseason.doors.column.store', kind: 'text', descriptionKey: 'inseason.doors.columnGuide.storeName' },
  { key: 'format_code', headerKey: 'inseason.doors.column.format', kind: 'text', descriptionKey: 'inseason.doors.columnGuide.format' },
  { key: 'climate_zone_code', headerKey: 'inseason.doors.column.climateZone', kind: 'text', descriptionKey: 'inseason.doors.columnGuide.climateZone' },
  { key: 'weeks_observed', headerKey: 'inseason.doors.column.weeksObserved', kind: 'number', descriptionKey: 'inseason.doors.columnGuide.weeksObserved' },
  // The file's own headers: the screen's «x of y» column is one cell, the file holds the two counts apart.
  { key: 'sizes_in_stock', headerKey: 'inseason.doors.exportColumn.sizesInStock', kind: 'number', descriptionKey: 'inseason.doors.columnGuide.sizesInStock' },
  { key: 'sizes_offered', headerKey: 'inseason.doors.column.sizesOffered', kind: 'number', descriptionKey: 'inseason.doors.columnGuide.sizesOffered' },
  { key: 'size_availability', headerKey: 'inseason.doors.column.availability', kind: 'number', descriptionKey: 'inseason.doors.columnGuide.availability' },
  // The average per observed week (issue 311): the read's on-hand is a sum over the weeks.
  { key: 'avg_on_hand_pairs', headerKey: 'inseason.doors.column.onHand', kind: 'number', descriptionKey: 'inseason.doors.columnGuide.onHand' },
  { key: 'pairs_sold', headerKey: 'inseason.doors.column.sold', descriptionKey: 'inseason.doors.columnGuide.sold', kind: 'number' },
  { key: 'weeks_of_cover', headerKey: 'inseason.doors.column.cover', kind: 'number', descriptionKey: 'inseason.doors.columnGuide.cover' },
  { key: 'live_articles_per_week', headerKey: 'inseason.doors.column.liveArticles', descriptionKey: 'inseason.doors.columnGuide.liveArticles', kind: 'number' },
];

const num = (value: number | null): ExportCell => (value === null ? { kind: 'missing', state: 'not-reported' } : { kind: 'number', raw: value });
const text = (value: string | null): ExportCell => (value === null ? { kind: 'missing', state: 'not-reported' } : { kind: 'text', value });

export function doorRow(door: DoorRow): readonly ExportCell[] {
  return [
    text(door.storeKey),
    // The page's own name: the platform's, else the store's key (`DoorsScreen`), never «n.d.» beside a name it draws.
    text(door.storeName ?? door.storeKey), text(door.format), text(door.climateZone), num(door.weeksObserved),
    num(door.sizesInStock), num(door.sizesOffered), num(door.availability), num(door.onHand), num(door.sold),
    num(door.cover), num(door.liveArticlesPerWeek),
  ];
}

const DEFINITION = 'bata-ui issue 307 / 308: per door over the run’s 13 complete weeks to its stock as-of week, size availability = sizes in stock ÷ sizes offered of the live articles (an article with at least one size in stock that week); weeks of cover = Σ on-hand pairs ÷ Σ pairs sold (none when the door sold nothing), as GET /inseason/runs/{run_id}/door-availability states them (bata-platform 570).';

function request(rows: readonly (readonly ExportCell[])[], filters: readonly ProvenanceFilter[], window: DoorAvailability | null): ExportRequest {
  return {
    id: DOORS_EXPORT_ID,
    titleKey: 'inseason.doors.exportTitle',
    columns: DOOR_COLUMNS,
    rows: () => rows,
    rowCount: rows.length,
    provenance: {
      filters,
      hierarchyNode: NOT_PUBLISHED,
      timeWindow: window === null ? NOT_PUBLISHED : { from: window.from, to: window.to },
      scenario: NOT_PUBLISHED,
      planVersionId: NOT_PUBLISHED,
      measureDefinitions: [{ measure: DOORS_EXPORT_ID, definition: DEFINITION, registerVersion: '' }],
      generatedAt: new Date().toISOString(),
      generatedBy: NO_PRINCIPAL,
      platformVersion: buildVersion(),
    },
    perimeter: { filters, sort: [], visibleColumns: DOOR_COLUMNS.map((column) => column.key), drillPath: [], densityMode: 'compact' },
  };
}

/**
 * Every door of the window, the table's order — the whole set, not the fifty the page draws
 * (ADR 9034). An empty file while the read is not pinned or no run is named, the filters saying so.
 */
export async function doorsExport(given?: ExportScope, read: DoorAvailabilityRead | null = DOOR_AVAILABILITY_READ): Promise<ExportRequest> {
  const runId = readNetworkHealthScope(given?.search ?? '').runId;
  const filters: ProvenanceFilter[] = [
    runId === null ? { field: 'run_id', operator: 'unset', value: '' } : { field: 'run_id', operator: 'equals', value: runId },
  ];
  if (read === null || runId === null) return request([], filters, null);
  const window = doorAvailabilityOf(await callOperation(read, doorAvailabilityParams(runId), {}));
  return request(rankDoors(window.doors).map(doorRow), filters, window);
}
